import React, { useEffect, useId, useRef, useState } from "react";
import { getPublishedVerifiedAddresses, initializePublicAddressPool, PUBLIC_ADDRESS_POOL_ID } from "../services/publicVerifiedAddresses";
import {
  readUsedVerifiedAddresses,
} from "../services/verifiedAddressStorage";
import { addressUsageId, claimNextSharedAddress, migrateLocalAddressUsage, subscribeSharedAddressUsage } from "../services/sharedAddressUsage";
import { isAllowedNtid } from "../services/allowedNtids";
import "./GetAddress.css";

const storageError = error => typeof error?.code === "string" && typeof error.message === "string"
  ? error.message : "Saved addresses could not be opened. Please try again.";

export const GetAddressPanel = () => {
  const zipId = useId();
  const [ntid, setNtid] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [claims, setClaims] = useState(null);
  const [allAddresses] = useState(() => [...new Map(getPublishedVerifiedAddresses().map(row => [addressUsageId(row), row])).values()]);
  const [selected, setSelected] = useState(null);
  const [issuedTo, setIssuedTo] = useState("");
  const [issuedAt, setIssuedAt] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [copying, setCopying] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const claimingRef = useRef(false);
  const selectionVersion = useRef(0);
  const mounted = useRef(false);
  const selectedId = useRef(null);
  selectedId.current = selected?.id || null;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; selectionVersion.current += 1; };
  }, []);

  useEffect(() => {
    return subscribeSharedAddressUsage(rows => {
      setClaims(rows);
      setError("");
    }, failure => { setClaims(null); setError(storageError(failure)); });
  }, []);

  const clearSelection = () => {
    selectionVersion.current += 1;
    setSelected(null);
    setIssuedTo("");
    setIssuedAt(null);
    setNotice("");
    setError("");
  };

  const usedIds = new Set((claims || []).map(row => addressUsageId(row.address)));
  const filtered = allAddresses.filter(row => !zipCode || row.zipCode === zipCode);
  const progress = claims ? { total: filtered.length, remaining: filtered.filter(row => !usedIds.has(addressUsageId(row))).length } : null;

  const getAddress = async event => {
    event?.preventDefault();
    if (claimingRef.current) return;
    clearSelection();
    const identity = ntid.trim().toLowerCase();
    if (!/^[a-z0-9]{3,32}$/.test(identity)) {
      setError("Enter your company NTID using 3–32 letters or numbers (for example, lkn01477).");
      return;
    }
    if (!isAllowedNtid(ntid)) {
      setSelected(null);
      setError("This NTID is not approved. Enter an approved company NTID.");
      return;
    }
    if (!progress || progress.remaining === 0) return;
    const version = selectionVersion.current;
    claimingRef.current = true;
    setClaiming(true);
    try {
      const next = await claimNextSharedAddress({ ntid: identity, zipCode });
      if (!mounted.current || version !== selectionVersion.current) return;
      if (!next) {
        setSelected(null);
        setNotice("No unused verified addresses are available for this ZIP code.");
        return;
      }
      setSelected(next.address);
      setIssuedTo(next.ntid);
      setIssuedAt(next.usedAt);
      setNotice(`Address saved to Used Addresses for ${next.ntid}.`);
    } catch (failure) {
      if (mounted.current && version === selectionVersion.current) setError(storageError(failure));
    } finally {
      claimingRef.current = false;
      if (mounted.current) setClaiming(false);
    }
  };

  const copyAddress = async () => {
    if (!selected || copying) return;
    const address = selected;
    setNotice(""); setError(""); setCopying(true);
    try {
      if (typeof navigator.clipboard?.writeText !== "function") throw new Error("Clipboard unavailable.");
      await navigator.clipboard.writeText(address.address);
      if (mounted.current && selectedId.current === address.id) setNotice("Address copied.");
    } catch (_failure) {
      if (mounted.current && selectedId.current === address.id) {
        setError("Could not copy automatically. Select the address text and copy it.");
      }
    } finally {
      if (mounted.current) setCopying(false);
    }
  };

  const zipCodes = [...new Set(allAddresses.map(row => row.zipCode))].sort();
  const empty = progress && allAddresses.length === 0;
  const exhausted = progress && progress.total > 0 && progress.remaining === 0;

  return (
    <section className="get-address-panel" aria-labelledby="get-address-title">
      <header className="get-address-heading">
        <div><h1 id="get-address-title">Get Address</h1><p>Enter your approved company NTID to get the next unused verified address.</p></div>
      </header>
        <form className="get-address-controls" onSubmit={getAddress}>
          <div className="get-address-ntid"><label htmlFor={`${zipId}-ntid`}>NTID <span aria-hidden="true">*</span></label>
            <input id={`${zipId}-ntid`} name="ntid" value={ntid} maxLength={32} required  autoComplete="off" autoCapitalize="none" spellCheck={false}
              onChange={event => { setNtid(event.target.value); clearSelection(); }} />
          </div>
          <div className="get-address-zip"><label htmlFor={`${zipId}-zip`}>ZIP code</label><select id={`${zipId}-zip`} value={zipCode}
            onChange={event => { setZipCode(event.target.value); clearSelection(); }}>
            <option value="">All ZIP codes</option>{zipCodes.map(zip => <option key={zip} value={zip}>{zip}</option>)}
          </select></div>
          <button className="get-address-primary" type="submit" disabled={!progress || progress.remaining === 0 || claiming}>{claiming ? "Saving…" : "Get Address"}</button>
          {progress && <p className="get-address-count" aria-live="polite"><strong>{progress.remaining}</strong> remaining <span>of {progress.total} saved{zipCode ? ` in ${zipCode}` : ""}</span></p>}
        </form>
        {error && <p className="get-address-error" role="alert">{error}</p>}
        {!progress && !error && <p className="get-address-message" role="status">Loading shared address usage…</p>}
        {empty && <p className="get-address-empty">No verified addresses are available yet.</p>}
        {progress && !empty && progress.total === 0 && <p className="get-address-message">No saved addresses match this ZIP code.</p>}
        {exhausted && <p className="get-address-message">All saved addresses{zipCode ? ` in ${zipCode}` : ""} have been given out.</p>}
        {selected && isAllowedNtid(issuedTo) && isAllowedNtid(ntid) && issuedTo === ntid.trim().toLowerCase()
          && (!zipCode || selected.zipCode === zipCode) && <article className="get-address-result" aria-labelledby="issued-address-title" aria-live="polite">
          <div className="get-address-result-heading"><h2 id="issued-address-title">Your address</h2><span>Verified eligible</span></div>
          <p className="get-address-value">{selected.address}</p>
          <dl><div><dt>NTID</dt><dd>{issuedTo || "Not recorded"}</dd></div><div><dt>Used on</dt><dd>{issuedAt ? <time dateTime={issuedAt}>{new Date(issuedAt).toLocaleString()}</time> : "Not recorded"}</dd></div>
            <div><dt>ZIP code</dt><dd>{selected.zipCode}</dd></div><div><dt>Provider</dt><dd>{selected.provider === "T-Mobile" ? "T-Mobile 5G" : selected.provider}</dd></div>
            <div><dt>Verified on</dt><dd><time dateTime={selected.checkedAt} title={selected.checkedAt}>{new Date(selected.checkedAt).toLocaleString()}</time></dd></div></dl>
          <button className="get-address-copy" type="button" onClick={copyAddress} disabled={copying}>{copying ? "Copying…" : "Copy address"}</button>
        </article>}
        {notice && <p className="get-address-notice" role="status">{notice}</p>}
    </section>
  );
};

const GetAddress = () => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    (async () => {
      initializePublicAddressPool();
      await migrateLocalAddressUsage(readUsedVerifiedAddresses(PUBLIC_ADDRESS_POOL_ID));
      if (active) setReady(true);
    })().catch(failure => { if (active) setError(storageError(failure)); });
    return () => { active = false; };
  }, []);
  return <main className="get-address-page">
    {ready ? <GetAddressPanel /> : <section className="get-address-panel" aria-labelledby="get-address-title">
      <h1 id="get-address-title">Get Address</h1>
      {error ? <p className="get-address-error" role="alert">{error}</p>
        : <p className="get-address-message" role="status">Loading saved addresses…</p>}
    </section>}
  </main>;
};

export default GetAddress;
